import React from 'react';

import { parseAddress, ParsedAddress } from 'vladdress';

import { formatHereMapsAddress } from './HereMapsAddressLink';
import { removeUnitedStatesFromAddress } from './util/removeUnitedStatesFromAddress';

export function formatAddress(
  address: string,
  containerClassName?: string,
  truncate = true
): React.ReactNode {
  try {
    const addressParts = parseAddress(removeUnitedStatesFromAddress(address));
    return renderAddress(addressParts, containerClassName);
  } catch {
    if (truncate) {
      return formatHereMapsAddress(address);
    } else {
      return address;
    }
  }
}

export function renderAddress(
  addressParts: Partial<ParsedAddress>,
  containerClassName?: string
) {
  const hasCityAndState =
    addressParts.placeName && addressParts.stateAbbreviation;
  const hasCityOnly = addressParts.placeName && !addressParts.stateAbbreviation;
  const hasStateOnly =
    !addressParts.placeName && addressParts.stateAbbreviation;

  const placeLine = (() => {
    if (hasCityAndState) {
      return `${addressParts.placeName}, ${addressParts.stateAbbreviation}`;
    }

    if (hasCityOnly) {
      return addressParts.placeName;
    }

    if (hasStateOnly) {
      return addressParts.stateAbbreviation;
    }

    return '';
  })();

  return (
    <div className={`flex flex-col ${containerClassName}`}>
      <div>{addressParts.addressLine1}</div>
      {addressParts.addressLine2 ? (
        <div>{addressParts.addressLine2}</div>
      ) : null}
      <div>{placeLine}</div>
      <div>{addressParts.zipCodePlusFour ?? addressParts.zipCode}</div>
    </div>
  );
}

/**
 * Renders an address with just the street + city
 * @return `123 main st, New York City`
 */
export function renderShortAddress(address: string) {
  const addressParts = parseAddress(removeUnitedStatesFromAddress(address));
  return `${addressParts.addressLine1}, ${addressParts.placeName}`;
}

export function safeRenderShortAddress(address: string) {
  try {
    return renderShortAddress(address);
  } catch {
    return address;
  }
}
